import { API, buildQueryParams, get, post, put, remove } from "api";
import { OK } from "http-status-codes";

export interface UserGroup {
    id: number;
    name: string;
}

export interface TemplateValidationResult {
    preview: string;
    error?: string;
}

export interface Template {
    name: string;
    auto: boolean;
    userGroups: UserGroup[];
}

export interface EmailTemplate {
    emailTemplates: Template[];
}

interface TemplateDetails {
    template: TemplateDefinition;
}

export interface TemplateDefinition {
    variables: string;
    emailType: string;
    proofType: string;
    language: string;
    cc: string;
    bcc: string;
    to: string;
    replyTo: string;
    from: string;
    subject: string;
    // html body - handled by 3rd party lib
    text?: string;
    smsText: string;
    cellPhone: string;
    status: string;
}

export interface TemplateViewModel {
    name: string;
    auto?: boolean;
    groups?: number[];
    body: TemplateDefinition;
}

export interface TemplateMaterialSpecModel {
    materialSpec: string;
    auto?: boolean;
    systemId?: string;
    body: TemplateDefinition;
}

export const getTemplates = async (headers: HeadersInit): Promise<Template[]> => {
    const { parsedBody } = await get<EmailTemplate>(`${API.baseApiUrl}/email-templates`, { headers });

    return parsedBody?.emailTemplates || [];
};

export const getTemplateDetails = async (
    templateName: string,
    headers: HeadersInit
): Promise<TemplateDefinition | undefined> => {
    const { parsedBody } = await get<TemplateDetails>(`${API.baseApiUrl}/email-templates/${templateName}`, {
        headers,
    });

    return parsedBody?.template;
};

export const createOrUpdateTemplate = async (
    { body, name, groups, auto = false }: TemplateViewModel,
    headers: HeadersInit
): Promise<number> => {
    const queryParams = buildQueryParams({ groups: groups?.join(",") || "", auto });

    await put<number>(`${API.baseApiUrl}/email-templates/${name}?${queryParams}`, body, {
        headers,
    });

    return OK;
};

export const deleteTemplate = async (templateName: string, headers: HeadersInit): Promise<number> => {
    await remove<number>(`${API.baseApiUrl}/email-templates/${templateName}`, {
        headers,
    });

    return OK;
};

export const validateTemplate = async (
    { body, materialSpec }: TemplateMaterialSpecModel,
    headers: HeadersInit
): Promise<TemplateValidationResult | undefined> => {
    const queryParams = buildQueryParams({ matspecname: materialSpec, auto: false });

    const { parsedBody } = await post<TemplateValidationResult>(
        `${API.baseApiUrl}/email-templates/validate?${queryParams}`,
        body,
        {
            headers,
        }
    );

    return parsedBody;
};

export const getUserGroups = async (headers: HeadersInit): Promise<UserGroup[]> => {
    const { parsedBody } = await get<{ userGroups: UserGroup[] }>(`${API.baseApiUrl}/email-templates/user-groups`, {
        headers,
    });

    return parsedBody?.userGroups || [];
};
